import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card } from '@/design-system/ui/Card';
import { Button } from '@/design-system/ui/Button';
import { Icon } from '@/design-system/ui/Icon';
import { EmptyState } from '@/design-system/ui/States';
import { cn } from '@/design-system/ui/cn';
import { useSEO } from '@/core/seo/useSEO';
import { articles } from '../data/magazine';

const ALL = 'همه';

const MAGAZINE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'مجله و دانشنامه سفر بین‌شهری بلینو',
  description: 'راهنمای جامع تاکسی بین‌شهری کرمان، مقایسه نرخ کرایه‌ها، معرفی ایستگاه‌ها و پایانه‌ها، و مقالات تخصصی حمل بار و مسافر در ایران.',
  url: 'https://blino.ir/magazine',
  hasPart: articles.map((a) => ({
    '@type': 'Article',
    headline: a.title,
    url: `https://blino.ir/magazine/${a.slug}`,
    description: a.excerpt,
  })),
};

export default function MagazinePage() {
  useSEO({
    title: 'مجله بلینو | راهنمای جامع تاکسی بین شهری کرمان، نرخ کرایه و ایستگاه‌ها',
    description: 'راهنمای صفر تا صد تاکسی بین شهری کرمان؛ نرخ کرایه سواری عادی و دربستی، شماره تماس رزرو، معرفی ایستگاه‌های آدینه، سرآسیاب و فیروزه، و مقالات تخصصی حمل بار بلینو.',
    canonical: '/magazine',
    keywords: 'مجله بلینو, تاکسی بین شهری کرمان, شماره تاکسی بین شهری کرمان, نرخ کرایه تاکسی بین شهری کرمان, ایستگاه سواری کرمان, رزرو تاکسی کرمان سیرجان رفسنجان, مهاجر تیکت',
    jsonLd: MAGAZINE_JSON_LD,
  });

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(ALL);

  const categories = useMemo(() => [ALL, ...new Set(articles.map((a) => a.category))], []);

  const countsByCategory = useMemo(() => {
    const counts: Record<string, number> = { [ALL]: articles.length };
    for (const a of articles) {
      counts[a.category] = (counts[a.category] ?? 0) + 1;
    }
    return counts;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      const matchCat = activeCategory === ALL || a.category === activeCategory;
      if (!matchCat) return false;
      if (!q) return true;
      const inTitle = a.title.toLowerCase().includes(q);
      const inExcerpt = a.excerpt.toLowerCase().includes(q);
      const inKeywords = a.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false;
      return inTitle || inExcerpt || inKeywords;
    });
  }, [search, activeCategory]);

  const featuredArticle = useMemo(() => {
    return articles.find((a) => a.featured) ?? articles[0];
  }, []);

  return (
    <div className="mx-auto max-w-none px-4 sm:px-8 lg:px-12 py-8 md:py-12">
      {/* Hero Header */}
      <header className="relative mb-10 overflow-hidden rounded-[28px] bg-hero p-6 text-white shadow-floating sm:p-10">
        <div className="pointer-events-none absolute -end-20 -top-20 size-72 rounded-full bg-[radial-gradient(circle,rgb(2_195_154/0.35),transparent_70%)]" aria-hidden />
        <div className="pointer-events-none absolute -bottom-20 -start-10 size-60 rounded-full bg-[radial-gradient(circle,rgb(251_191_36/0.2),transparent_70%)]" aria-hidden />

        <div className="relative z-10 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-mint backdrop-blur-md">
            <Icon name="sparkles" size={15} />
            دانشنامه تخصصی سفر و حمل‌ونقل
          </span>
          <h1 className="mt-3 text-2xl font-bold leading-tight sm:text-4xl">
            مجله بلینو؛ مرجع تاکسی و سفر بین‌شهری
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-white/85 sm:text-base">
            راهنمای جامع تاکسی بین‌شهری کرمان، جداول به‌روز نرخ کرایه، معرفی ایستگاه‌ها و پایانه‌ها، و مقالات تخصصی ترابری هوشمند جاده‌ای.
          </p>

          {/* Quick Trust Highlights */}
          <div className="mt-6 flex flex-wrap gap-2 text-xs font-medium text-white/90">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 backdrop-blur-sm">
              <Icon name="verified" size={16} className="text-mint" />
              راهنمای به‌روزرسانی‌شده ۱۴۰۵
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 backdrop-blur-sm">
              <Icon name="route" size={16} className="text-mint" />
              پوشش کامل مسیرهای کرمان
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 backdrop-blur-sm">
              <Icon name="ticket" size={16} className="text-mint" />
              رزرو ۱۰۰٪ آنلاین صندلی
            </span>
          </div>
        </div>
      </header>

      {/* Featured Article Banner */}
      {featuredArticle && activeCategory === ALL && !search && (
        <section className="mb-10" aria-label="مقاله ویژه">
          <Link to={`/magazine/${featuredArticle.slug}`} className="group block">
            <Card interactive className="relative overflow-hidden border-2 border-action/20 bg-paper p-6 transition-all duration-300 group-hover:border-action group-hover:shadow-glow sm:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="warning" className="font-bold">
                  ★ مقاله برگزیده
                </Badge>
                <Badge tone="info">{featuredArticle.category}</Badge>
                <span className="inline-flex items-center gap-1 text-xs text-ink-3">
                  <Icon name="clock" size={14} />
                  {featuredArticle.readTime} مطالعه
                </span>
                <span className="text-xs text-ink-3">· {featuredArticle.publishedAt}</span>
              </div>

              <h2 className="mt-3 text-xl font-bold leading-snug text-ink transition-colors group-hover:text-action sm:text-2xl">
                {featuredArticle.title}
              </h2>

              <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-ink-2 sm:text-base">
                {featuredArticle.excerpt}
              </p>

              {featuredArticle.keyTakeaways && (
                <ul className="mt-4 grid gap-1.5 rounded-xl bg-fill/60 p-3.5 text-xs text-ink-2 sm:grid-cols-2">
                  {featuredArticle.keyTakeaways.slice(0, 2).map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-0.5 text-action">✓</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-5 flex items-center justify-between pt-2">
                <span className="inline-flex items-center gap-1 text-sm font-bold text-action transition group-hover:translate-x-[-3px]">
                  <span>مطالعه راهنمای کامل</span>
                  <Icon name="chevronLeft" size={16} />
                </span>
                <span className="text-xs text-ink-3">تیم تحریریه و ترابری بلینو</span>
              </div>
            </Card>
          </Link>
        </section>
      )}

      {/* Search & Category Filter Bar */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="دسته‌بندی مقالات">
          {categories.map((c) => {
            const isSelected = activeCategory === c;
            return (
              <button
                key={c}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setActiveCategory(c)}
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-all sm:text-sm',
                  isSelected
                    ? 'bg-cta text-white shadow-glow'
                    : 'bg-primary-light text-navy hover:bg-line',
                )}
              >
                <span>{c}</span>
                <span className={cn('rounded-full px-1.5 py-0.2 text-[11px]', isSelected ? 'bg-white/25 text-white' : 'bg-white/70 text-ink-2')}>
                  {countsByCategory[c] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Live Search Input */}
        <div className="relative w-full sm:w-72">
          <span className="pointer-events-none absolute inset-y-0 start-3 grid place-items-center text-ink-3">
            <Icon name="search" size={18} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو در مقالات..."
            className="w-full rounded-full border border-line bg-paper py-2 pe-3 ps-9 text-sm text-ink outline-none transition focus:border-action focus:ring-2 focus:ring-action/20"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="پاک کردن جستجو"
              className="absolute inset-y-0 end-3 grid place-items-center text-ink-3 hover:text-ink"
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Article Listing Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          title="مقاله‌ای یافت نشد"
          description="با تغییر عبارت جستجو یا انتخاب دسته‌بندی دیگر دوباره امتحان کنید."
          action={
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setActiveCategory(ALL);
              }}
            >
              نمایش همه مقالات
            </Button>
          }
        />
      ) : (
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <li key={a.id}>
              <Link to={`/magazine/${a.slug}`} className="group block h-full">
                <Card interactive className="flex h-full flex-col p-5 transition-all duration-200 group-hover:border-action/40 group-hover:shadow-md">
                  <div className="mb-3 flex items-center justify-between">
                    <Badge tone="info" className="text-xs">
                      {a.category}
                    </Badge>
                    <span className="inline-flex items-center gap-1 text-[11px] text-ink-3">
                      <Icon name="clock" size={14} />
                      {a.readTime}
                    </span>
                  </div>

                  <h2 className="mb-2 line-clamp-2 font-bold leading-snug text-ink transition-colors group-hover:text-action">
                    {a.title}
                  </h2>

                  <p className="mb-4 line-clamp-3 flex-1 text-xs leading-relaxed text-ink-2">
                    {a.excerpt}
                  </p>

                  <div className="flex items-center justify-between border-t border-line/60 pt-3 text-xs">
                    <span className="text-ink-3">{a.publishedAt}</span>
                    <span className="inline-flex items-center gap-1 font-bold text-action transition group-hover:translate-x-[-2px]">
                      <span>مطالعه</span>
                      <Icon name="chevronLeft" size={14} />
                    </span>
                  </div>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* Interactive Booking Banner at Bottom */}
      <section className="mt-14 overflow-hidden rounded-[24px] bg-gradient-to-br from-primary-light via-paper to-primary-light p-6 text-center shadow-sm sm:p-8" aria-label="رزرو آنلاین">
        <span className="inline-block rounded-full bg-cta/15 px-3 py-1 text-xs font-bold text-cta">
          سامانه هوشمند تاکسی بین‌شهری بلینو
        </span>
        <h2 className="mt-2 text-xl font-bold text-navy sm:text-2xl">
          قصد سفر بین‌شهری از کرمان یا سایر شهرها را دارید؟
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-xs leading-relaxed text-ink-2 sm:text-sm">
          صندلی خود را آنلاین انتخاب کنید، از قیمت قطعی و شفاف مطلع شوید و بدون معطلی در ترمینال یا سرما و گرما سفر خود را آغاز نمایید.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Link to="/#search">
            <Button size="lg" icon={<Icon name="search" size={18} />}>
              جستجو و رزرو بلیط
            </Button>
          </Link>
          <Link to="/door-to-door">
            <Button variant="outline" size="lg" icon={<Icon name="pin" size={18} />}>
              سفر درب‌به‌درب
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
